import React, { useContext, useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import { DataContext } from '@aytm/app/stats/page/sidebar/reports/ReportsPanel'

import { STATIC_REPORTS_IDS, VIRTUAL_REPORTS_IDS, updateItemModel } from '@aytm/app/stats/page/sidebar/reports/actions'

import {
  isSkipperReport,
  isSkipperGuidedUnsettledReport
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'

// components
import { Checkbox } from 'primereact/checkbox'

import ReportPopupSection from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupSection'
import ReportPopupAppliedFilters from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupAppliedFilters'
import { ReportPopupSkipperSettings } from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupAnalysisSettings'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const getDateApproximation = timestamp => {
  const timeZone = PageGlobals.TIME_ZONE || {}
  const localDate = moment(1000 * timestamp).utcOffset(
    Object.hasOwn(timeZone, 'offset') ? timeZone.offset : moment().utcOffset()
  )

  const minutesDiff = Math.floor(moment().diff(localDate, 'minutes'))
  const hoursDiff = Math.floor(moment().diff(localDate, 'hours'))
  const daysDiff = Math.floor(moment().diff(localDate, 'days'))
  const monthsDiff = Math.floor(moment().diff(localDate, 'months'))
  const yearsDiff = Math.floor(moment().diff(localDate, 'years'))

  if (minutesDiff < 1) {
    return `just now`
  }
  if (hoursDiff < 1) {
    return `${minutesDiff} ${1 === minutesDiff ? 'minute' : 'minutes'} ago`
  }
  if (daysDiff < 1) {
    return `${hoursDiff} ${1 === hoursDiff ? 'hour' : 'hours'} ago`
  }
  if (monthsDiff < 1) {
    return `${daysDiff} ${1 === daysDiff ? 'day' : 'days'} ago`
  }
  if (yearsDiff < 1) {
    return `${monthsDiff} ${1 === monthsDiff ? 'month' : 'months'} ago`
  }
  return `${yearsDiff} ${1 === yearsDiff ? 'year' : 'years'} ago`
}

// Accordion section keys for the description column.
const SECTION_FILTERS = 'filters'
const SECTION_ANALYSIS = 'analysis'

let timestampInterval = null

const ReportPopupDescription = React.forwardRef(({ model, position }, ref) => {
  const {
    listModel,
    features,
    isAdmin,
    isSurveyGuest,
    isSurveyViewer,
    isSurveyCompleted,
    dispatch,
    getReportFilters,
    onReportsChange,
    onReportsError
  } = useContext(DataContext)

  // a Skipper Report is opened to read its settings; every other report has only the filters
  const [openSection, setOpenSection] = useState(isSkipperReport(model.current) ? SECTION_ANALYSIS : SECTION_FILTERS)

  // opening a section closes any other; clicking the open one closes it
  const toggleSection = key => setOpenSection(prev => (prev === key ? null : key))

  const createdTimestampRef = useRef(null)
  const editedTimestampRef = useRef(null)

  const getModelProps = () => {
    return {
      url: PageGlobals.STAT_REPORT_URL,
      listModel,
      model,
      onReportsChange,
      onReportsError
    }
  }

  const onHiddenChange = () => {
    updateItemModel(dispatch, {
      ...getModelProps(),
      data: {
        do: 'flags',
        id: model.id,
        flags: { hidden: !model.current.hidden, protected: model.current.protected }
      },
      updates: [{ path: 'hidden', value: !model.current.hidden }]
    })
  }

  const onProtectedChange = () => {
    updateItemModel(dispatch, {
      ...getModelProps(),
      data: {
        do: 'flags',
        id: model.id,
        flags: { hidden: model.current.hidden, protected: !model.current.protected }
      },
      updates: [{ path: 'protected', value: !model.current.protected }]
    })
  }

  useEffect(() => {
    if (timestampInterval) clearInterval(timestampInterval)
    timestampInterval = setInterval(() => {
      if (createdTimestampRef.current) {
        createdTimestampRef.current.innerHTML = getDateApproximation(model.current.created)
      }
      if (editedTimestampRef.current) {
        editedTimestampRef.current.innerHTML = getDateApproximation(model.current.edited)
      }
    }, 60000)

    return () => {
      if (timestampInterval) clearInterval(timestampInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.current])

  const isDefault = 1 == position
  const isLocked =
    ['original', 'widget', ...VIRTUAL_REPORTS_IDS].includes(model.current.type) ||
    isSkipperGuidedUnsettledReport(model.current)
  // computed per report when the popup opens, instead of eagerly for every report
  const { filters = [], customizations = [] } = getReportFilters ? getReportFilters(model.id) : {}
  const creator = model.current.creator
  const editor = model.current.editor

  return (
    <div ref={ref} className={style.description}>
      <div className={style['description-header']}>
        {!['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(model.current.type) &&
          'number' === typeof model.current.created && (
            <>
              <div className={style['description-header-head']}>Created by</div>
              <div
                data-testid="stat-reports-report-popup-description-created"
                className={style['description-header-body']}
              >
                {!isSurveyGuest && !isSurveyViewer && 'string' == typeof creator && 0 < creator.length ? (
                  <>
                    <b>
                      <i className="icon-aytmfd-client-laptop" />
                      {creator}
                    </b>
                    {` `}
                  </>
                ) : null}
                {/* the space after the name is real text, so the line reads "name just now" rather than gluing them */}
                <span ref={createdTimestampRef}>{getDateApproximation(model.current.created)}</span>
              </div>
            </>
          )}
        {!['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(model.current.type) &&
          'number' === typeof model.current.edited && (
            <>
              <div className={style['description-header-head']}>Modified by</div>
              <div
                data-testid="stat-reports-report-popup-description-modified"
                className={style['description-header-body']}
              >
                {!isSurveyGuest && !isSurveyViewer && 'string' == typeof editor && 0 < editor.length ? (
                  <>
                    <b>
                      <i className="icon-aytmfd-client-laptop" />
                      {editor}
                    </b>
                    {` `}
                  </>
                ) : null}
                <span ref={editedTimestampRef}>{getDateApproximation(model.current.edited)}</span>
              </div>
            </>
          )}
      </div>
      <div className={style['description-content']}>
        <ReportPopupSection
          title="Applied filters & customizations"
          open={SECTION_FILTERS === openSection}
          onToggle={() => toggleSection(SECTION_FILTERS)}
        >
          <div data-testid="stat-reports-report-popup-description-filters">
            <ReportPopupAppliedFilters filters={filters} />
          </div>
          {0 < customizations.length ? (
            <div data-testid="stat-reports-report-popup-description-customizations">
              <ReportPopupAppliedFilters filters={customizations} />
            </div>
          ) : null}
        </ReportPopupSection>
        <ReportPopupSkipperSettings
          report={model.current}
          features={features}
          open={SECTION_ANALYSIS === openSection}
          isSurveyCompleted={isSurveyCompleted}
          onToggle={() => toggleSection(SECTION_ANALYSIS)}
        />
      </div>
      {isAdmin && !isLocked ? (
        <div className={style['description-footer']}>
          {isDefault ? null : (
            <>
              <Checkbox
                data-testid="stat-reports-report-popup-description-invisible"
                className="p-checkbox-sm"
                aria-labelledby={`stat-reports-report-${model.id}-popup-description-invisible-label`}
                value="Invisible to client"
                checked={model.current.hidden}
                onChange={onHiddenChange}
              />
              <span
                id={`stat-reports-report-${model.id}-popup-description-invisible-label`}
                className={style['description-footer-label']}
                onClick={onHiddenChange}
              >
                <i className="icon-aytmfd-invisible" />
                Invisible
              </span>
            </>
          )}
          {['xpert', ...STATIC_REPORTS_IDS].includes(model.current.type) ? null : (
            <>
              <Checkbox
                data-testid="stat-reports-report-popup-description-unkillable"
                className="p-checkbox-sm"
                aria-labelledby={`stat-reports-report-${model.id}-popup-description-unkillable-label`}
                value="Unkillable"
                checked={model.current.protected}
                onChange={onProtectedChange}
              />
              <span
                id={`stat-reports-report-${model.id}-popup-description-unkillable-label`}
                className={style['description-footer-label']}
                onClick={onProtectedChange}
              >
                <i className="icon-aytmfd-lock" />
                Unkillable
              </span>
            </>
          )}
        </div>
      ) : null}
    </div>
  )
})

ReportPopupDescription.propTypes = {
  model: PropTypes.object.isRequired,
  position: PropTypes.number
}

ReportPopupDescription.displayName = 'ReportPopupDescription'

export default ReportPopupDescription
